import moment from "moment";
import OrderFilterFieldDatepicker from "@/components/OrderFilter/Field/OrderFilterFieldDatepicker";
import OrderFilterFieldCheckbox from "@/components/OrderFilter/Field/OrderFilterFieldCheckbox";
import OrderFilterFieldSelect from "@/components/OrderFilter/Field/OrderFilterFieldSelect";
import OrderFilterFieldSelectBasic from "@/components/OrderFilter/Field/OrderFilterFieldSelectBasic";
import OrderFilterFieldAutocomplete from "@/components/OrderFilter/Field/OrderFilterFieldAutocomplete";
import OrderFilterFieldInput from "@/components/OrderFilter/Field/OrderFilterFieldInput";
import OrderFilterFieldTags from "@/components/OrderFilter/Field/OrderFilterFieldTags";
import OrderFilterFieldStatus from "@/components/OrderFilter/Field/OrderFilterFieldStatus";
import OrderFilterFieldHasAsset from "@/components/OrderFilter/Field/OrderFilterFieldHasAsset";
import OrderFilterFieldKpiStatus from "@/components/OrderFilter/Field/OrderFilterFieldKpiStatus";
import OrderFilterFieldKpiAppealStatus from "@/components/OrderFilter/Field/OrderFilterFieldKpiAppealStatus";
import { statusGroups } from "@/helpers/statuses";
import { AssetService } from "@/services/asset.service";
import { CodeService } from "@/services/code.service";
import { FirmService } from "@/services/firm.service";
import { OrderService } from "@/services/order.service.js";
import { TagService } from "@/services/tag.service";
import { UserService } from "@/services/user.service.js";
import { ContractService } from "@/services/contract.service.js";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";

export const orderFilterMixin = {
  props: {
    filters: {
      type: Object
    },
    billingCode: {
      type: Array
    },
    assetCode: {
      type: Array
    },
    managersId: {
      type: Array
    },
    width: {
      type: Number,
      default: 50
    }
  },
  components: {
    OrderFilterFieldDatepicker,
    OrderFilterFieldCheckbox,
    OrderFilterFieldSelect,
    OrderFilterFieldSelectBasic,
    OrderFilterFieldAutocomplete,
    OrderFilterFieldInput,
    OrderFilterFieldTags,
    OrderFilterFieldStatus,
    OrderFilterFieldHasAsset,
    OrderFilterFieldKpiStatus,
    OrderFilterFieldKpiAppealStatus
  },
  data: () => ({
    assetService: AssetService,
    codeService: CodeService,
    firmService: FirmService,
    orderService: OrderService,
    tagService: TagService,
    userService: UserService,
    filters_: {},
    inputs: {
      dueDatePreset: "",
      lastModified: "",
      commentCreatedAtStart: null,
      commentCreatedAtEnd: null,
      releaseDateStart: null,
      releaseDateEnd: null,
      requiredFinishStart: null,
      requiredFinishEnd: null
    },
    options: {
      predefinedDates: [
        {
          value: 0,
          label: "Today"
        },
        {
          value: 1,
          label: "Yesterday"
        },
        {
          value: 3,
          label: "Last 3 days"
        },
        {
          value: 7,
          label: "This week"
        },
        {
          value: 14,
          label: "Last week"
        }
      ],
      assetCodes: [],
      managers: [],
      siteRevisitRequired: [
        {
          value: 1,
          label: "Yes"
        },
        {
          value: 0,
          label: "No"
        }
      ],
      billingCodes: [],
      claimLinesTotalComparison: [
        {
          label: ">=",
          value: ">="
        },
        {
          label: "=",
          value: "="
        },
        {
          label: "<=",
          value: "<="
        }
      ]
    },
    // Map filter_ key and value so that it will be easier to check/uncheck checkboxes on tags input add/remove tag
    tagCheckboxes: {
      billingOnHold: "BILLING ON HOLD",
      bigStorm: "STORM EVENT",
      furtherWorkRequired: "FURTHER WORK REQUIRED",
      doNotSurvey: "DO NOT SURVEY",
      escalated: "ESCALATED",
      surchargeManhole: "SURCHARGED MANHOLE"
    },
    tags: []
  }),
  mounted() {},
  computed: {
    defaultFields() {
      return [
        {
          id: "status",
          component: "OrderFilterFieldStatus",
          value: this.filters_.comment,
          props: { active: !!this.filters.comment },
          onInput: value => (this.filters_.comment = value)
        },
        {
          id: "statusChangeStart",
          component: "OrderFilterFieldDatepicker",
          value: this.filters_.commentCreatedAtStart,
          props: {
            label: "Status Change - Start",
            active: !!this.filters.commentCreatedAtStart,
            disabled: !!!this.filters_.comment
          },
          onInput: value => (this.filters_.commentCreatedAtStart = value)
        },
        {
          id: "statusChangeEnd",
          component: "OrderFilterFieldDatepicker",
          value: this.filters_.commentCreatedAtEnd,
          props: {
            label: "Status Change - End",
            active: !!this.filters.commentCreatedAtEnd,
            disabled: !!!this.filters_.comment
          },
          onInput: value => (this.filters_.commentCreatedAtEnd = value)
        },
        {
          id: "includeVoid",
          component: "OrderFilterFieldCheckbox",
          value: !!this.filters_.includeVoid,
          props: {
            label: "Include Void",
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.includeVoid = value)
        },
        {
          id: "readyToComplete",
          component: "OrderFilterFieldCheckbox",
          value: !!this.filters_.readyToComplete,
          props: {
            label: "Ready To Complete",
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.readyToComplete = value)
        },
        {
          id: "readyToClaim",
          component: "OrderFilterFieldCheckbox",
          value: !!this.filters_.readyToClaim,
          props: {
            label: "Ready To Claim",
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.readyToClaim = value)
        },
        {
          id: "pendingDateChange",
          component: "OrderFilterFieldCheckbox",
          value: !!this.filters_.dateChangeRequest,
          props: {
            label: "Pending Date Change Requests",
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.dateChangeRequest = value)
        },
        {
          id: "unvettedNotes",
          component: "OrderFilterFieldCheckbox",
          value: !!this.filters_.unvettedNotes,
          props: {
            label: "Notes Pending Review",
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.unvettedNotes = value)
        },
        {
          id: "starredOrders",
          component: "OrderFilterFieldCheckbox",
          value: !!this.filters_.starred,
          props: {
            label: "Starred Orders",
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.starred = value)
        },
        {
          id: "pendingClaimApproval",
          component: "OrderFilterFieldCheckbox",
          value: !!this.filters_.pendingApproval,
          props: {
            label: "Pending Claim Approval",
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.pendingApproval = value)
        },
        {
          id: "inDispute",
          component: "OrderFilterFieldCheckbox",
          value: !!this.filters_.inDispute,
          props: {
            label: "Was Disputed",
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.inDispute = value)
        },
        {
          id: "currentlyInDispute",
          component: "OrderFilterFieldCheckbox",
          value: !!this.filters_.currentlyInDispute,
          props: {
            label: "Currently Disputed",
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.currentlyInDispute = value)
        },
        {
          id: "textSearch",
          component: "OrderFilterFieldInput",
          value: this.filters_.search,
          props: {
            label: "WO Number / Text Search",
            name: "order_search",
            active: !!this.filters.search
          },
          onInput: value => (this.filters_.search = value)
        },
        {
          id: "contractType",
          component: "OrderFilterFieldSelect",
          value: this.filters_.contractType,
          props: {
            service: ContractService,
            fetchFunc: "listTypes",
            label: "Contract Type",
          },
          ref: "selectContractType",
          onInput: value => (this.filters_.contractType = value)
        },
        {
          id: "contractId",
          component: "OrderFilterFieldSelect",
          value: this.filters_.contractId,
          props: {
            service: ContractService,
            fetchFunc: "all",
            fetchParams: {
              type_tag_id: this.filters_.contractType,
              includes: 'contractor,attributes',
            },
            label: "Contract",
            labelKey: "displayName",
            active: !!this.filters.contractId,
            customFilter: this.filterContracts,
          },
          ref: "selectContractId",
          onInput: value => (this.filters_.contractId = value)
        },
        // {
        //   id: "startCodeTypeValue",
        //   component: "OrderFilterFieldSelect",
        //   value: this.filters_.startCodeTypeValue,
        //   props: {
        //     service: ContractService,
        //     fetchFunc: "getCodeSets",
        //     denormalize: false,
        //     label: "Code Set",
        //     valueKey: 'value',
        //     labelKey: 'text',
        //     active: !!this.filters.startCodeTypeValue,
        //     preselectOnOne: true
        //   },
        //   onInput: value => (this.filters_.startCodeTypeValue = value)
        // },
        {
          id: "issuedBy",
          component: "OrderFilterFieldSelect",
          value: this.filters_.issuedBy,
          props: {
            service: this.firmService,
            fetchFunc: "parents",
            fetchParams: { permissions: ["can issue orders"] },
            label: "Issued By",
            active: !!this.filters.issuedBy,
            preselectOnOne: true
          },
          onInput: value => (this.filters_.issuedBy = value)
        },
        {
          id: "issuedTo",
          component: "OrderFilterFieldSelect",
          value: this.filters_.issuedTo,
          props: {
            service: this.orderService,
            fetchFunc: "filters",
            fetchParams: { fields: ["issued_to"] },
            responseHandler: this.makeResponseHandler('issued_to'),
            label: "Issued To",
            active: !!this.filters.issuedTo,
            preselectOnOne: true
          },
          onInput: value => (this.filters_.issuedTo = value)
        },
        // {
        //   id: "type",
        //   component: "OrderFilterFieldSelect",
        //   value: this.filters_.type,
        //   props: {
        //     service: this.codeService,
        //     fetchFunc: "all",
        //     fetchParams: {
        //       type: "orderType",
        //       contract_id: this.filters_.contractId,
        //       firm_id: this.filters_.contractId ? undefined : this.userFirmId,
        //       // type: this.filters_.startCodeTypeValue
        //     },
        //     label: "Type",
        //     name: "order_type",
        //     active: !!this.filters.type,
        //     // fetchOnInit: false,
        //     // defaultOptionLabel: this.filters_.startCodeTypeValue
        //     //   ? "All"
        //     //   : "Please Select",
        //     // disabled: !this.filters_.startCodeTypeValue
        //   },
        //   ref: "selectType",
        //   onInput: value => (this.filters_.type = value)
        // },
        {
          id: "type",
          component: "OrderFilterFieldSelect",
          value: this.filters_.type,
          props: {
            service: this.codeService,
            fetchFunc: "all",
            fetchParams: {
              type: "orderType",
              contract_id: this.filters_.contractId || undefined,
              contract_include_archived: this.filters_.contractId ? 1 : undefined,
              firm_id: this.filters_.contractId ? undefined : this.userFirmId,
              firm_include_archived: this.filters_.contractId ? undefined : 1,
              order_by: "code",
              sort_by: "asc",
            },
            label: "Type",
            name: "order_type",
            customLabel: option => `[${option.code}] ${option.name}`,
            grouped: true,
            groupBy: "archived_at",
            groupLabels: {
              null: "ACTIVE",
              default: "ARCHIVED",
            },
            active: !!this.filters.type,
          },
          ref: "selectType",
          onInput: value => (this.filters_.type = value)
        },
        // {
        //   id: "subType",
        //   component: "OrderFilterFieldSelect",
        //   value: this.filters_.subType,
        //   props: {
        //     service: this.codeService,
        //     fetchFunc: "all",
        //     fetchParams: {
        //       type: "ACTIVITYTYPE",
        //       contract_id: this.filters_.contractId,
        //       firm_id: this.filters_.contractId ? undefined : this.userFirmId,
        //       // start_code_type: this.filters_.startCodeTypeValue,
        //     },
        //     label: "Sub Type",
        //     name: "order_subtype",
        //     active: !!this.filters.subType,
        //     // fetchOnInit: false,
        //     // defaultOptionLabel: this.filters_.startCodeTypeValue
        //     //   ? "All"
        //     //   : "Please Select",
        //     // disabled: !this.filters_.startCodeTypeValue
        //   },
        //   ref: "selectSubType",
        //   onInput: value => (this.filters_.subType = value)
        // },
        {
          id: "subType",
          component: "OrderFilterFieldSelect",
          value: this.filters_.subType,
          props: {
            service: this.codeService,
            fetchFunc: "all",
            fetchParams: {
              type: "ACTIVITYTYPE",
              contract_id: this.filters_.contractId || undefined,
              contract_include_archived: this.filters_.contractId ? 1 : undefined,
              firm_id: this.filters_.contractId ? undefined : this.userFirmId,
              firm_include_archived: this.filters_.contractId ? undefined : 1,
              order_by: "code",
              sort_by: "asc",
            },
            label: "Sub Type",
            name: "order_subtype",
            customLabel: option => `[${option.code}] ${option.name}`,
            grouped: true,
            groupBy: "archived_at",
            groupLabels: {
              null: "ACTIVE",
              default: "ARCHIVED",
            },
            active: !!this.filters.subType,
          },
          ref: "selectSubType",
          onInput: value => (this.filters_.subType = value)
        },
        {
          id: "priority",
          component: "OrderFilterFieldSelect",
          value: this.filters_.priority,
          props: {
            service: this.orderService,
            fetchFunc: "listPriorities",
            valueKey: "priority",
            labelKey: "priority",
            label: "Priority",
            active: !!this.filters.priority
          },
          onInput: value => (this.filters_.priority = value)
        },
        {
          id: "region",
          component: "OrderFilterFieldSelect",
          value: this.filters_.regionTagId,
          props: {
            service: this.tagService,
            fetchFunc: "all",
            fetchParams: {
              type: "[ORDER][MANDATORY]",
              firm_id: this.selectedContract ? this.selectedContract.contractee_firm_id : this.userFirmId,
            },
            label: "Region",
            active: !!this.filters.regionTagId
          },
          onInput: value => (this.filters_.regionTagId = value)
        },
        // {
        //   id: "problemCode",
        //   component: "OrderFilterFieldSelect",
        //   value: this.filters_.problemCodeId,
        //   props: {
        //     service: this.codeService,
        //     fetchFunc: "all",
        //     fetchParams: {
        //       type: "PROBLEM",
        //       contract_id: this.filters_.contractId,
        //       firm_id: this.filters_.contractId ? undefined : this.userFirmId,
        //       // start_code_type: this.filters_.startCodeTypeValue,
        //     },
        //     label: "Problem Code",
        //     active: !!this.filters.problemCodeId,
        //     // fetchOnInit: false,
        //     // defaultOptionLabel: this.filters_.startCodeTypeValue
        //     //   ? "All"
        //     //   : "Please Select",
        //     // disabled: !this.filters_.startCodeTypeValue
        //   },
        //   ref: "selectProblemCode",
        //   onInput: value => (this.filters_.problemCodeId = value)
        // },
        {
          id: "problemCode",
          component: "OrderFilterFieldSelect",
          value: this.filters_.problemCodeId,
          props: {
            service: this.codeService,
            fetchFunc: "all",
            fetchParams: {
              type: "PROBLEM",
              contract_id: this.filters_.contractId || undefined,
              contract_include_archived: this.filters_.contractId ? 1 : undefined,
              firm_id: this.filters_.contractId ? undefined : this.userFirmId,
              firm_include_archived: this.filters_.contractId ? undefined : 1,
              order_by: "code",
              sort_by: "asc",
            },
            label: "Problem Code",
            customLabel: option => `[${option.code}] ${option.name}`,
            grouped: true,
            groupBy: "archived_at",
            groupLabels: {
              null: "ACTIVE",
              default: "ARCHIVED",
            },
            active: !!this.filters.problemCodeId,
          },
          ref: "selectProblemCode",
          onInput: value => (this.filters_.problemCodeId = value)
        },
        // {
        //   id: "activityCode",
        //   component: "OrderFilterFieldSelect",
        //   value: this.filters_.activityCode,
        //   props: {
        //     service: this.codeService,
        //     fetchFunc: "all",
        //     fetchParams: {
        //       type: "ACTIVITY",
        //       contract_id: this.filters_.contractId,
        //       firm_id: this.filters_.contractId ? undefined : this.userFirmId,
        //       // start_code_type: this.filters_.startCodeTypeValue,
        //     },
        //     label: "Activity Code",
        //     active: !!this.filters.activityCode,
        //     // fetchOnInit: false,
        //     // defaultOptionLabel: this.filters_.startCodeTypeValue
        //     //   ? "All"
        //     //   : "Please Select",
        //     // disabled: !this.filters_.startCodeTypeValue
        //   },
        //   ref: "selectActivityCode",
        //   onInput: value => (this.filters_.activityCode = value)
        // },
        {
          id: "activityCode",
          component: "OrderFilterFieldSelect",
          value: this.filters_.activityCode,
          props: {
            service: this.codeService,
            fetchFunc: "all",
            fetchParams: {
              type: "ACTIVITY",
              contract_id: this.filters_.contractId || undefined,
              contract_include_archived: this.filters_.contractId ? 1 : undefined,
              firm_id: this.filters_.contractId ? undefined : this.userFirmId,
              firm_include_archived: this.filters_.contractId ? undefined : 1,
              order_by: "code",
              sort_by: "asc",
            },
            label: "Activity Code",
            customLabel: option => `[${option.code}] ${option.name}`,
            grouped: true,
            groupBy: "archived_at",
            groupLabels: {
              null: "ACTIVE",
              default: "ARCHIVED",
            },
            active: !!this.filters.activityCode,
          },
          ref: "selectActivityCode",
          onInput: value => (this.filters_.activityCode = value)
        },
        {
          id: "releaseDateStart",
          component: "OrderFilterFieldDatepicker",
          value: this.filters_.releaseDateStart,
          props: {
            label: "Date Logged - Start",
            active: !!this.filters.releaseDateStart
          },
          onInput: value => (this.filters_.releaseDateStart = value)
        },
        {
          id: "releaseDateEnd",
          component: "OrderFilterFieldDatepicker",
          value: this.filters_.releaseDateEnd,
          props: {
            label: "Date Logged - End",
            active: !!this.filters.releaseDateEnd
          },
          onInput: value => (this.filters_.releaseDateEnd = value)
        },
        {
          id: "dueDatePreset",
          component: "OrderFilterFieldSelectBasic",
          value: this.inputs.dueDatePreset,
          props: {
            options: this.options.predefinedDates,
            label: "Due Date Preset",
            defaultOption: "None"
          },
          onInput: value => (this.inputs.dueDatePreset = value)
        },
        {
          id: "dueDateStart",
          component: "OrderFilterFieldDatepicker",
          value: this.filters_.requiredFinishStart,
          props: {
            label: "Due Date - Start",
            active: !!this.filters.requiredFinishStart,
            columnSize: { "is-6": this.width >= 25, "is-12": this.width < 25 }
          },
          onInput: value => (this.filters_.requiredFinishStart = value)
        },
        {
          id: "dueDateEnd",
          component: "OrderFilterFieldDatepicker",
          value: this.filters_.requiredFinishEnd,
          props: {
            label: "Due Date - End",
            active: !!this.filters.requiredFinishEnd,
            columnSize: { "is-6": this.width >= 25, "is-12": this.width < 25 }
          },
          onInput: value => (this.filters_.requiredFinishEnd = value)
        },
        {
          id: "locationAsset",
          component: "OrderFilterFieldAutocomplete",
          value: this.filters_.locationAssetId,
          props: {
            service: this.assetService,
            fetchFunc: "all",
            fetchParams: {
              code_code: "SITE",
              per_page: 1000,
              contract_ids: this.filters_.contractId ? [this.filters_.contractId] : undefined,
            },
            fetchOnInit: false,
            label: "Location Asset",
            labelKey: "",
            //customFormatter: option => `[${option.code.code}] ${option.assetName}`,
            customFormatter: option => `${option.assetName}`,
            disabled: !this.filters_.contractId,
          },
          ref: "autocompleteLocationAsset",
          onInput: value => (this.filters_.locationAssetId = value)
        },
        {
          id: "hasAsset",
          component: "OrderFilterFieldHasAsset",
          value: this.filters_.hasAsset,
          onInput: value => (this.filters_.hasAsset = value)
        },
        // {
        //   id: "assetCode",
        //   component: "OrderFilterFieldSelectBasic",
        //   value: this.filters_.assetCode,
        //   props: {
        //     options: this.options.assetCodes,
        //     label: "Asset Code",
        //     active: !!this.filters.assetCode,
        //     disabled: !this.filters_.hasAsset,
        //     defaultOption: "All"
        //   },
        //   onInput: value => (this.filters_.assetCode = value)
        // },
        {
          id: "assetCode",
          component: "OrderFilterFieldSelect",
          value: this.filters_.assetCode,
          props: {
            service: this.codeService,
            fetchFunc: "all",
            fetchParams: {
              type: "ASSET",
              contract_id: this.filters_.contractId || undefined,
              contract_include_archived: this.filters_.contractId ? 1 : undefined,
              firm_id: this.filters_.contractId ? undefined : this.userFirmId,
              firm_include_archived: this.filters_.contractId ? undefined : 1,
              order_by: "code",
              sort_by: "asc",
            },
            label: "Asset Code",
            defaultOptionLabel: "All",
            customLabel: option => `[${option.code}] ${option.name}`,
            grouped: true,
            groupBy: "archived_at",
            groupLabels: {
              null: "ACTIVE",
              default: "ARCHIVED",
            },
            active: !!this.filters.assetCode,
            disabled: !this.filters_.hasAsset,
          },
          ref: "selectAssetCode",
          onInput: value => (this.filters_.assetCode = value)
        },
        {
          id: "assetThirdPartyCode",
          component: "OrderFilterFieldInput",
          value: this.filters_.assetThirdPartyCode,
          props: {
            label: "Asset Third Party Code",
            active: !!this.filters.assetThirdPartyCode,
            disabled: !this.filters_.hasAsset,
          },
          onInput: value => (this.filters_.assetThirdPartyCode = value)
        },
        {
          id: "lastModified",
          component: "OrderFilterFieldSelectBasic",
          value: this.inputs.lastModified,
          props: {
            options: this.options.predefinedDates,
            label: "Last Modified",
            active: !!this.filters.modifiedAtStart,
            defaultOption: "Not Applicable"
          },
          onInput: value => (this.inputs.lastModified = value)
        },
        {
          id: "managedBy",
          component: "OrderFilterFieldSelectBasic",
          value: this.filters_.managed_by,
          props: {
            options: this.options.managers,
            label: "Managed By",
            active: !!this.filters.managed_by,
            defaultOption: "All"
          },
          onInput: value => (this.filters_.managed_by = value)
        },
        {
          id: "assignedTo",
          component: "OrderFilterFieldSelect",
          value: this.filters_.assignedTo,
          props: {
            service: this.userService,
            fetchFunc: "all",
            fetchParams: {
              firmId: this.userFirmId,
              filter_by: "first_name",
              active: 1,
              archived: false
            },
            label: "Assigned To",
            active: !!this.filters.assignedTo,
            customLabel: option => option.firstName
          },
          onInput: value => (this.filters_.assignedTo = value)
        },
        {
          id: "siteRevisitRequired",
          component: "OrderFilterFieldSelectBasic",
          value: this.filters_.returnToSite,
          props: {
            options: this.options.siteRevisitRequired,
            label: "Site Revisit Required",
            active: !!this.filters.returnToSite,
            defaultOption: "Not Applicable"
          },
          onInput: value => (this.filters_.returnToSite = value)
        },
        {
          id: "claimId", // component: "OrderFilterFieldSelectBasic",
          component: "OrderFilterFieldSelect",
          value: this.filters_.billingCode,
          props: {
            service: this.orderService,
            fetchFunc: "listBillingCodes",
            valueKey: "billing_code",
            labelKey: "billing_code",
            label: "Claim ID", // options: this.options.billingCodes,
            active: !!this.filters.billingCode,
            defaultOption: "All"
          },
          ref: "selectClaimId",
          onInput: value => (this.filters_.billingCode = value)
        },
        // {
        //   id: "outcomeCode",
        //   component: "OrderFilterFieldSelect",
        //   value: this.filters_.outcomeCodeId,
        //   props: {
        //     service: this.codeService,
        //     fetchFunc: "all",
        //     fetchParams: {
        //       type: "REACTIVE",
        //       contract_id: this.filters_.contractId,
        //       firm_id: this.filters_.contractId ? undefined : this.userFirmId,
        //       // start_code_type: this.filters_.startCodeTypeValue,
        //     },
        //     label: "Outcome Code",
        //     active: !!this.filters.outcomeCodeId,
        //     columnSize: { "is-6": this.width >= 25, "is-12": this.width < 25 },
        //     // fetchOnInit: false,
        //     // defaultOptionLabel: this.filters_.startCodeTypeValue
        //     //   ? "All"
        //     //   : "Please Select",
        //     // disabled: !this.filters_.startCodeTypeValue
        //   },
        //   ref: "selectOutcomeCode",
        //   onInput: value => (this.filters_.outcomeCodeId = value)
        // },
        {
          id: "outcomeCode",
          component: "OrderFilterFieldSelect",
          value: this.filters_.outcomeCodeId,
          props: {
            service: this.codeService,
            fetchFunc: "all",
            fetchParams: {
              type: "REACTIVE",
              contract_id: this.filters_.contractId || undefined,
              contract_include_archived: this.filters_.contractId ? 1 : undefined,
              firm_id: this.filters_.contractId ? undefined : this.userFirmId,
              firm_include_archived: this.filters_.contractId ? undefined : 1,
              order_by: "code",
              sort_by: "asc",
            },
            label: "Outcome Code",
            customLabel: option => `[${option.code}] ${option.name}`,
            grouped: true,
            groupBy: "archived_at",
            groupLabels: {
              null: "ACTIVE",
              default: "ARCHIVED",
            },
            active: !!this.filters.outcomeCodeId,
            columnSize: { "is-6": this.width >= 25, "is-12": this.width < 25 },
          },
          ref: "selectOutcomeCode",
          onInput: value => (this.filters_.outcomeCodeId = value)
        },
        // {
        //   id: "responsibilityCode",
        //   component: "OrderFilterFieldSelect",
        //   value: this.filters_.responsibilityCodeId,
        //   props: {
        //     service: this.codeService,
        //     fetchFunc: "all",
        //     fetchParams: {
        //       type: "RESPONSE",
        //       contract_id: this.filters_.contractId,
        //       firm_id: this.filters_.contractId ? undefined : this.userFirmId,
        //       // start_code_type: this.filters_.startCodeTypeValue,
        //     },
        //     label: "Responsibility Code",
        //     active: !!this.filters.responsibilityCodeId,
        //     columnSize: { "is-6": this.width >= 25, "is-12": this.width < 25 },
        //     // fetchOnInit: false,
        //     // defaultOptionLabel: this.filters_.startCodeTypeValue
        //     //   ? "All"
        //     //   : "Please Select",
        //     // disabled: !this.filters_.startCodeTypeValue
        //   },
        //   ref: "selectResponsibilityCode",
        //   onInput: value => (this.filters_.responsibilityCodeId = value)
        // },
        {
          id: "responsibilityCode",
          component: "OrderFilterFieldSelect",
          value: this.filters_.responsibilityCodeId,
          props: {
            service: this.codeService,
            fetchFunc: "all",
            fetchParams: {
              type: "RESPONSE",
              contract_id: this.filters_.contractId || undefined,
              contract_include_archived: this.filters_.contractId ? 1 : undefined,
              firm_id: this.filters_.contractId ? undefined : this.userFirmId,
              firm_include_archived: this.filters_.contractId ? undefined : 1,
              order_by: "code",
              sort_by: "asc",
            },
            label: "Responsibility Code",
            customLabel: option => `[${option.code}] ${option.name}`,
            grouped: true,
            groupBy: "archived_at",
            groupLabels: {
              null: "ACTIVE",
              default: "ARCHIVED",
            },
            active: !!this.filters.responsibilityCodeId,
            columnSize: { "is-6": this.width >= 25, "is-12": this.width < 25 },
          },
          ref: "selectResponsibilityCode",
          onInput: value => (this.filters_.responsibilityCodeId = value)
        },
        // {
        //   id: "failureCode",
        //   component: "OrderFilterFieldSelect",
        //   value: this.filters_.failureCodeId,
        //   props: {
        //     service: this.codeService,
        //     fetchFunc: "all",
        //     fetchParams: {
        //       type: "FAILURE",
        //       contract_id: this.filters_.contractId,
        //       firm_id: this.filters_.contractId ? undefined : this.userFirmId,
        //       // start_code_type: this.filters_.startCodeTypeValue,
        //     },
        //     label: "Failure Code",
        //     active: !!this.filters.failureCodeId,
        //     columnSize: { "is-6": this.width >= 25, "is-12": this.width < 25 },
        //     // fetchOnInit: false,
        //     // defaultOptionLabel: this.filters_.startCodeTypeValue
        //     //   ? "All"
        //     //   : "Please Select",
        //     // disabled: !this.filters_.startCodeTypeValue
        //   },
        //   ref: "selectFailureCode",
        //   onInput: value => (this.filters_.failureCodeId = value)
        // },
        {
          id: "failureCode",
          component: "OrderFilterFieldSelect",
          value: this.filters_.failureCodeId,
          props: {
            service: this.codeService,
            fetchFunc: "all",
            fetchParams: {
              type: "FAILURE",
              contract_id: this.filters_.contractId || undefined,
              contract_include_archived: this.filters_.contractId ? 1 : undefined,
              firm_id: this.filters_.contractId ? undefined : this.userFirmId,
              firm_include_archived: this.filters_.contractId ? undefined : 1,
              order_by: "code",
              sort_by: "asc",
            },
            label: "Failure Code",
            customLabel: option => `[${option.code}] ${option.name}`,
            grouped: true,
            groupBy: "archived_at",
            groupLabels: {
              null: "ACTIVE",
              default: "ARCHIVED",
            },
            active: !!this.filters.failureCodeId,
            columnSize: { "is-6": this.width >= 25, "is-12": this.width < 25 },
          },
          ref: "selectFailureCode",
          onInput: value => (this.filters_.failureCodeId = value)
        },
        // {
        //   id: "repairCode",
        //   component: "OrderFilterFieldSelect",
        //   value: this.filters_.repairCodeId,
        //   props: {
        //     service: this.codeService,
        //     fetchFunc: "all",
        //     fetchParams: {
        //       type: "REPAIR",
        //       contract_id: this.filters_.contractId,
        //       firm_id: this.filters_.contractId ? undefined : this.userFirmId,
        //       // start_code_type: this.filters_.startCodeTypeValue,
        //     },
        //     label: "Repair Code",
        //     active: !!this.filters.repairCodeId,
        //     columnSize: { "is-6": this.width >= 25, "is-12": this.width < 25 },
        //     // fetchOnInit: false,
        //     // defaultOptionLabel: this.filters_.startCodeTypeValue
        //     //   ? "All"
        //     //   : "Please Select",
        //     // disabled: !this.filters_.startCodeTypeValue
        //   },
        //   ref: "selectRepairCode",
        //   onInput: value => (this.filters_.repairCodeId = value)
        // },
        {
          id: "repairCode",
          component: "OrderFilterFieldSelect",
          value: this.filters_.repairCodeId,
          props: {
            service: this.codeService,
            fetchFunc: "all",
            fetchParams: {
              type: "REPAIR",
              contract_id: this.filters_.contractId || undefined,
              contract_include_archived: this.filters_.contractId ? 1 : undefined,
              firm_id: this.filters_.contractId ? undefined : this.userFirmId,
              firm_include_archived: this.filters_.contractId ? undefined : 1,
              order_by: "code",
              sort_by: "asc",
            },
            label: "Repair Code",
            customLabel: option => `[${option.code}] ${option.name}`,
            grouped: true,
            groupBy: "archived_at",
            groupLabels: {
              null: "ACTIVE",
              default: "ARCHIVED",
            },
            active: !!this.filters.repairCodeId,
            columnSize: { "is-6": this.width >= 25, "is-12": this.width < 25 },
          },
          ref: "selectRepairCode",
          onInput: value => (this.filters_.repairCodeId = value)
        },
        {
          id: "billingOnHold",
          component: "OrderFilterFieldCheckbox",
          value: this.filters_.billingOnHold,
          props: {
            label: "Billing On Hold",
            trueValue: this.tagCheckboxes.billingOnHold,
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.billingOnHold = value)
        },
        {
          id: "bigStorm",
          component: "OrderFilterFieldCheckbox",
          value: this.filters_.bigStorm,
          props: {
            label: "Storm Event",
            trueValue: this.tagCheckboxes.bigStorm,
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.bigStorm = value)
        },
        {
          id: "escalated",
          component: "OrderFilterFieldCheckbox",
          value: this.filters_.escalated,
          props: {
            label: "Escalated",
            trueValue: this.tagCheckboxes.escalated,
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.escalated = value)
        },
        {
          id: "furtherWorkRequired",
          component: "OrderFilterFieldCheckbox",
          value: this.filters_.furtherWorkRequired,
          props: {
            label: "Further Work Required",
            trueValue: this.tagCheckboxes.furtherWorkRequired,
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.furtherWorkRequired = value)
        },
        {
          id: "doNotSurvey",
          component: "OrderFilterFieldCheckbox",
          value: this.filters_.doNotSurvey,
          props: {
            label: "Do Not Survey",
            trueValue: this.tagCheckboxes.doNotSurvey,
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.doNotSurvey = value)
        },
        {
          id: "surchargeManhole",
          component: "OrderFilterFieldCheckbox",
          value: this.filters_.surchargeManhole,
          props: {
            label: "Surcharged Manhole",
            trueValue: this.tagCheckboxes.surchargeManhole,
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.surchargeManhole = value)
        },
        {
          id: "hasComments",
          component: "OrderFilterFieldCheckbox",
          value: this.filters_.hasComments,
          props: {
            label: "Has Comments",
            trueValue: "Yes",
            columnSize: {
              "is-4": this.width >= 40,
              "is-6": this.width >= 25 && this.width < 40,
              "is-12": this.width < 25
            }
          },
          onInput: value => (this.filters_.hasComments = value)
        },
        {
          id: "tags",
          component: "OrderFilterFieldTags",
          value: this.tagsAll,
          props: {
            active: !!(this.filters.tags && this.filters.tags.length),
            columnSize: { "is-6": this.width >= 30, "is-12": this.width < 30 },
            onAdd: this.onTagsInputAdd,
            onRemove: this.onTagsInputRemove
          },
          onInput: () => {}
        },
        {
          id: "claimLinesTotal",
          component: "OrderFilterFieldInput",
          value: this.filters_.claimLinesTotal,
          props: {
            label: "Value",
            active: !!this.filters.claimLinesTotal,
            columnSize: {
              "is-3": this.width >= 30,
              "is-6": this.width >= 15 && this.width < 30,
              "is-12": this.width < 15
            }
          },
          onInput: value => (this.filters_.claimLinesTotal = value)
        },
        {
          id: "claimLinesTotalComparison",
          component: "OrderFilterFieldSelectBasic",
          value: this.filters_.claimLinesTotalComparison,
          props: {
            options: this.options.claimLinesTotalComparison,
            label: "Comparison",
            active: !!this.filters.claimLinesTotalComparison,
            columnSize: {
              "is-3": this.width >= 30,
              "is-6": this.width >= 15 && this.width < 30,
              "is-12": this.width < 15
            },
            defaultOption: ""
          },
          onInput: value => (this.filters_.claimLinesTotalComparison = value)
        },
        {
          id: "kpiStart",
          component: "OrderFilterFieldKpiStatus",
          value: this.filters_.kpiStart,
          props: {
            label: "KPI Status Start Date",
            active: !!this.filters.kpiStart,
            columnSize: { "is-4": this.width >= 25, "is-12": this.width < 25 }
          },
          onInput: value => (this.filters_.kpiStart = value)
        },
        {
          id: "kpiFinish",
          component: "OrderFilterFieldKpiStatus",
          value: this.filters_.kpiFinish,
          props: {
            label: "KPI Status Finish Date",
            active: !!this.filters.kpiFinish,
            columnSize: { "is-4": this.width >= 25, "is-12": this.width < 25 }
          },
          onInput: value => (this.filters_.kpiFinish = value)
        },
        {
          id: "kpiAppeal",
          component: "OrderFilterFieldKpiAppealStatus",
          value: this.filters_.kpiAppeal,
          props: {
            active: !!this.filters.kpiAppeal,
            columnSize: { "is-4": this.width >= 25, "is-12": this.width < 25 }
          },
          onInput: value => (this.filters_.kpiAppeal = value)
        }
      ];
    },
    userFirmId() {
      return this.$store.state.Auth.user.firm.id;
    },
    tagsAll() {
      const tags = [];

      if (this.filters_.billingOnHold) {
        tags.push(this.filters_.billingOnHold);
      }

      if (this.filters_.bigStorm) {
        tags.push(this.filters_.bigStorm);
      }

      if (this.filters_.furtherWorkRequired) {
        tags.push(this.filters_.furtherWorkRequired);
      }

      if (this.filters_.doNotSurvey) {
        tags.push(this.filters_.doNotSurvey);
      }

      if (this.filters_.escalated) {
        tags.push(this.filters_.escalated);
      }

      if (this.filters_.surchargeManhole) {
        tags.push(this.filters_.surchargeManhole);
      }

      tags.push(...this.tags);

      return tags;
    },
    selectedContract() {
      if ( ! this.$refs.selectContractId) return;
      const contract_id = this.filters_.contractId;
      const contract = this.$refs.selectContractId[0].$refs.baseSelect.options.find(c => {
        return (c.id == contract_id);
      });
      return contract;
    },
    selectedContractStartCodeTypeValue() {
      if (this.selectedContract && this.selectedContract.id) {
        // Get the contract's start code type
        const attributes = this.selectedContract.attributes || [];
        const attr = attributes.find(a => {
          return a.code.toUpperCase() == '[SYSTEM][ORDER][CODE_CHAIN][START]';
        });
        return attr ? attr.value : null;
      }
    }
  },
  watch: {
    filters: {
      handler: function(filters) {
        this.filters_ = {
          ...this.filters_,
          ...filters
        };

        this.inputs.releaseDateStart = filters.releaseDateStart
          ? new Date(
              moment(filters.releaseDateStart).format("MM/DD/YYYY HH:mm:ss")
            )
          : null;
        // this.inputs.releaseDateEnd = filters.releaseDateEnd ? new Date(moment(filters.releaseDateEnd).format("MM/DD/YYYY HH:mm:ss")) : null;
      },

      deep: true,
      immediate: true
    },
    "filters_.comment": function(val) {
      if (val) {
        let includeVoid = false;
        let voidLabels = ["CLOSED_D", "CLOSED_N", "REJECTED", "INCC_ASSIGNED"];
        val.forEach((v, k) => {
          if (voidLabels.includes(v)) {
            includeVoid = true;
          }
        });
        this.filters_.includeVoid = includeVoid;

        this.filters_.readyToComplete =
          val.length === 1 && val.includes("PHYSICALLY_COMPLETED");
        this.filters_.readyToClaim =
          val.length === 1 && val.includes("COMPLETED");
        this.filters_.pendingApproval =
          val.length === 1 && val.includes("BLRQ");
      }

      if (!val) {
        this.filters_.commentCreatedAtStart = "";
        this.filters_.commentCreatedAtEnd = "";
        this.inputs.commentCreatedAtStart = null;
        this.inputs.commentCreatedAtEnd = null;
      }
    },
    "filters_.readyToComplete": function(val) {
      if (val) {
        this.filters_.comment = ["PHYSICALLY_COMPLETED"];
      }
    },
    "filters_.readyToClaim": function(val) {
      if (val) {
        this.filters_.comment = ["COMPLETED"];
      }
    },
    "filters_.pendingApproval": function(val) {
      if (val) {
        this.filters_.comment = ["BLRQ"];
      }
    },
    "filters_.requiredFinishStart": function(val) {
      if (val) {
        this.filters_.comment = "";
        this.$nextTick(() => {
          this.filters_.comment = statusGroups["OPEN"];
        });
      }
    },
    "filters_.contractType": function (next, prev) {
      this.$nextTick(() => {
        this.$refs.selectContractId[0].fetchOptions();
      });
    },
    "filters_.contractId": function (next, prev) {
      const contract = this.selectedContract;
      this.filters_.issuedBy = contract ? contract.contractee_firm_id.toString() : '';
      this.filters_.issuedTo = contract ? contract.contractor_firm_id.toString() : '';
      // if (next) {
      //   this.filters_.startCodeTypeValue = this.selectedContractStartCodeTypeValue
      //     ? this.selectedContractStartCodeTypeValue
      //     : '';
      // }
      this.$nextTick(() => {
        this.$refs.selectType[0].fetchOptions();
        this.$refs.selectSubType[0].fetchOptions();
        this.$refs.selectProblemCode[0].fetchOptions();
        this.$refs.selectActivityCode[0].fetchOptions();
        this.$refs.selectOutcomeCode[0].fetchOptions();
        this.$refs.selectResponsibilityCode[0].fetchOptions();
        this.$refs.selectFailureCode[0].fetchOptions();
        this.$refs.selectRepairCode[0].fetchOptions();
        this.$refs.selectAssetCode[0].fetchOptions();
        this.$refs.autocompleteLocationAsset[0].fetchOptions();
      });
    },
    // "filters_.startCodeTypeValue": function (next, prev) {
    //   this.filters_.issuedBy = "";
    //   this.filters_.issuedTo = "";
    //   if (next != this.selectedContractStartCodeTypeValue) {
    //     if (this.filters_.contractId != "") {
    //       this.filters_.contractId = "";
    //     }
    //   }
    //   this.$nextTick(() => {
    //     if (!next) {
    //       this.$refs.selectType[0].clearOptions();
    //       this.$refs.selectSubType[0].clearOptions();
    //       this.$refs.selectProblemCode[0].clearOptions();
    //       this.$refs.selectActivityCode[0].clearOptions();
    //       this.$refs.selectOutcomeCode[0].clearOptions();
    //       this.$refs.selectResponsibilityCode[0].clearOptions();
    //       this.$refs.selectFailureCode[0].clearOptions();
    //       this.$refs.selectRepairCode[0].clearOptions();
    //       return;
    //     }

    //     this.$refs.selectType[0].fetchOptions();
    //     this.$refs.selectSubType[0].fetchOptions();
    //     this.$refs.selectProblemCode[0].fetchOptions();
    //     this.$refs.selectActivityCode[0].fetchOptions();
    //     this.$refs.selectOutcomeCode[0].fetchOptions();
    //     this.$refs.selectResponsibilityCode[0].fetchOptions();
    //     this.$refs.selectFailureCode[0].fetchOptions();
    //     this.$refs.selectRepairCode[0].fetchOptions();
    //   });
    // },
    "filters_.issuedBy": function(val) {
      // Code change triggering moved to startCodeTypeValue
      /*
      this.$nextTick(() => {
        if (!val) {
          this.$refs.selectType[0].clearOptions();
          this.$refs.selectSubType[0].clearOptions();
          this.$refs.selectProblemCode[0].clearOptions();
          this.$refs.selectActivityCode[0].clearOptions();
          this.$refs.selectOutcomeCode[0].clearOptions();
          this.$refs.selectResponsibilityCode[0].clearOptions();
          this.$refs.selectFailureCode[0].clearOptions();
          this.$refs.selectRepairCode[0].clearOptions();
          return;
        }

        this.$refs.selectType[0].fetchOptions();
        this.$refs.selectSubType[0].fetchOptions();
        this.$refs.selectProblemCode[0].fetchOptions();
        this.$refs.selectActivityCode[0].fetchOptions();
        this.$refs.selectOutcomeCode[0].fetchOptions();
        this.$refs.selectResponsibilityCode[0].fetchOptions();
        this.$refs.selectFailureCode[0].fetchOptions();
        this.$refs.selectRepairCode[0].fetchOptions();
      });
      */
    },
    "filters_.issuedTo": function(val) {
      this.$emit("select-issued-to", val);
    },
    "inputs.dueDatePreset": function(val) {
      if (Number.isInteger(val)) {
        const start = moment()
          .subtract(val, "day")
          .toDate();
        const end = moment().toDate();
        this.inputs.requiredFinishStart = start;
        this.inputs.requiredFinishEnd = end;
        this.onDateInput("requiredFinishStart", start);
        this.onDateInput("requiredFinishEnd", end);
      } else {
        this.filters_.requiredFinishStart = "";
        this.filters_.requiredFinishEnd = "";
        this.inputs.requiredFinishStart = null;
        this.inputs.requiredFinishEnd = null;
      }
    },
    "inputs.lastModified": function(val) {
      if (Number.isInteger(val)) {
        this.filters_.modifiedAtStart = moment()
          .subtract(val, "day")
          .format("YYYY-MM-DD");
        this.filters_.modifiedAtEnd = moment().format("YYYY-MM-DD");
      } else {
        this.filters_.modifiedAtStart = "";
        this.filters_.modifiedAtEnd = "";
      }
    },
    managersId() {
      this.fetchManagers();
    },
    assetCode(assetCode) {
      this.options.assetCodes = assetCode.map(code => ({
        value: code.asset_id,
        label: `[ ${code.code} ] ${code.name}`
      }));
    },
    billingCode(billingCode) {
      this.options.billingCodes = billingCode.map(code => ({
        value: code,
        label: code
      }));
    }
  },
  methods: {
    filter() {
      // Set tag filter
      this.filters_.tags = this.tagsAll;
      this.$emit("filter", this.filters_);
    },
    reset() {
      this.resetInputs();
      this.$emit("reset");
    },
    resetInputs() {
      this.inputs.dueDatePreset = "";
      this.inputs.lastModified = "";
      this.inputs.commentCreatedAtStart = null;
      this.inputs.commentCreatedAtEnd = null;
      this.inputs.releaseDateStart = null;
      this.inputs.releaseDateEnd = null;
      this.inputs.requiredFinishStart = null;
      this.inputs.requiredFinishEnd = null;
      // this.$refs.selectType.reset();
      // this.$refs.selectSubType.reset();
      // this.$refs.selectProblemCodes.reset();
      // this.$refs.selectPriorities.reset();
      // this.$refs.selectClaims.reset();
      // this.$refs.selectOutcomeCodes.reset();
      // this.$refs.selectResponsibilityCodes.reset();
      // this.$refs.selectFailureCodes.reset();
      // this.$refs.selectRepairCodes.reset();
      // this.$refs.selectRegions.reset();
    },
    fetchSelectOptions(ref = null) {
      if (!ref) return;
      this.$refs[ref][0].fetchOptions();
    },
    onDateInput(filterKey, val, format = "YYYY-MM-DD") {
      if (val) {
        this.filters_[filterKey] = moment(val).format(format);
      }
    },
    onDateClear(filterKey) {
      this.inputs[filterKey] = null;
      this.filters_[filterKey] = "";
    },
    formatDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    filterTags(tags) {
      this.filters_.tags = tags;
    },
    onTagsInputAdd(tag) {
      const key = this.getKeyByValue(this.tagCheckboxes, tag);

      if (key) {
        this.toggleTagCheckboxes(key);
      } else {
        this.tags.push(tag);
      }
    },
    onTagsInputRemove(tag) {
      const key = this.getKeyByValue(this.tagCheckboxes, tag);

      if (key) {
        this.toggleTagCheckboxes(key);
      } else {
        const index = this.tags.findIndex(tag_ => tag_ === tag);

        if (index > -1) {
          this.tags.splice(index, 1);
        }
      }
    },
    toggleTagCheckboxes(key) {
      if (key) {
        if (this.filters_[key]) {
          this.$set(this.filters_, key, false);
        } else {
          this.$set(this.filters_, key, this.tagCheckboxes[key]);
        }
      }
    },
    getKeyByValue(obj, value) {
      return Object.keys(obj).find(key => obj[key] === value);
    },
    onStatusInput(status) {
      // Check if one of the status is included in void group
      // const voidGroup = statusGroups.VOID;
      // for (let x = 0; x < status.length; x++) {
      //   if (voidGroup.includes(status[x])) {
      //     // Set include void filter to true
      //     this.filters_.includeVoid = true;
      //     break;
      //   }
      // }
    },
    // fetchManagers() {
    //   UserService.userById({ users: this.managersId }).then(response => {
    //     const managers = JsonDenormalizer.denormalize(response);

    //     this.options.managers = managers.map(manager => ({
    //       value: manager.id,
    //       label: manager.fullName
    //     }));
    //   });
    // }
    fetchManagers() {
      UserService.getManagers().then(response => {
        const managers = JsonDenormalizer.denormalize(response);
        this.options.managers = managers.map(manager => ({
          value: manager.id,
          label: manager.fullName
        }));
      });
    },
    makeResponseHandler(field) {
      return function(response) {
        const target = `data.${field}`;
        const data = _.get(response.data, target);
        return JsonDenormalizer.denormalize(data);
      }
    },
    filterContracts(options) {
      // let filtered = options.filter(o => {
      //   let attributes = o.attributes || [];
      //   let attr = attributes.find(a => {
      //     return (a.code.toUpperCase() == '[SYSTEM][ORDER][CODE_CHAIN][START]');
      //   });
      //   return attr ? true : false;
      // });
      let filtered = options;
      // Modify display name
      filtered = filtered.map(v => {
        return {
          ...v,
          displayName: `${v.contractor.name} - ${v.name}`,
        };
      });
      filtered = _.orderBy(filtered, ["archived_at", "displayName"], ["desc", "asc"]);
      return filtered;
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/OrderFilter/mixins/orderFilterMixin.js